import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Loader2, CalendarDays, CalendarClock, RefreshCw, BadgeEuro, Layers, Repeat, AlertTriangle, Send } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { inAvviso, testoScadenza } from "../lib/abbonamento";

function fmtDate(v) {
  if (!v) return "—";
  const s = String(v).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return s;
}

function fmtEuro(v) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString("it-IT", { style: "currency", currency: "EUR" });
}

function Row({ icon: Icon, label, value, testId }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-slate-800/70 last:border-0" data-testid={testId}>
      <span className="flex items-center gap-2 text-sm text-slate-400">
        <Icon className="h-4 w-4 text-slate-500" /> {label}
      </span>
      <span className="text-sm font-semibold text-slate-100 text-right" data-testid={`${testId}-value`}>{value}</span>
    </div>
  );
}

function ScadenzaAlert({ abb }) {
  const g = inAvviso(abb);
  if (g == null) return null;
  const red = g < 0;
  return (
    <div data-testid="abbonamento-avviso-scadenza"
      className={`mt-3 flex items-center gap-2 rounded-lg border px-4 py-3 text-sm font-medium ${red ? "border-red-500/50 bg-red-500/10 text-red-400" : "border-orange-500/50 bg-orange-500/10 text-orange-400"}`}>
      <AlertTriangle className="h-4 w-4 shrink-0" /> {testoScadenza(g)}
    </div>
  );
}

function RinnovoButton({ abb }) {
  const { user } = useAuth();
  const [last, setLast] = useState(abb.ultima_richiesta_at || null);
  const [busy, setBusy] = useState(false);
  if (user?.role !== "titolare") return null;
  const recent = last && Date.now() - new Date(last).getTime() < 86400000;
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/studio/abbonamento/richiedi-rinnovo");
      setLast(data.created_at);
      toast.success("Richiesta di rinnovo inviata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-4 flex flex-wrap items-center gap-3">
      <button type="button" onClick={send} disabled={busy || recent} data-testid="abbonamento-richiedi-rinnovo"
        className="inline-flex items-center gap-2 rounded-md bg-amber-500 px-4 py-2 text-sm font-semibold text-[#0a0d14] transition-colors hover:bg-amber-600 disabled:opacity-60 disabled:cursor-not-allowed">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Richiedi rinnovo
      </button>
      {last && (
        <span className="text-xs text-slate-400" data-testid="abbonamento-richiesta-inviata">
          Richiesta inviata il {new Date(last).toLocaleDateString("it-IT")} alle {new Date(last).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
        </span>
      )}
    </div>
  );
}

export default function AbbonamentoCard() {
  const [loading, setLoading] = useState(true);
  const [abb, setAbb] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/studio/abbonamento")
      .then(({ data }) => { if (alive) setAbb(data || null); })
      .catch(() => { if (alive) setAbb(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="mt-4 flex items-center gap-2 text-sm text-slate-500" data-testid="abbonamento-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Caricamento abbonamento…
      </div>
    );
  }

  if (!abb) {
    return (
      <p className="mt-4 text-sm text-slate-500 border border-dashed border-slate-700 rounded-lg px-4 py-6 text-center" data-testid="abbonamento-empty">
        Nessun abbonamento attivo
      </p>
    );
  }

  return (
    <>
    <div className="mt-4 rounded-lg border border-slate-800 bg-slate-900/40 px-4 py-2" data-testid="abbonamento-card">
      <Row icon={Layers} label="Piano attivo" value={abb.piano || "—"} testId="abbonamento-piano" />
      <Row icon={BadgeEuro} label="Importo pagato" value={fmtEuro(abb.importo)} testId="abbonamento-importo" />
      <Row icon={CalendarDays} label="Data inizio abbonamento" value={fmtDate(abb.data_inizio)} testId="abbonamento-inizio" />
      <Row icon={CalendarClock} label="Data scadenza" value={fmtDate(abb.data_scadenza)} testId="abbonamento-scadenza" />
      <Row icon={RefreshCw} label="Data prossimo rinnovo" value={fmtDate(abb.prossimo_rinnovo)} testId="abbonamento-rinnovo" />
      <Row icon={Repeat} label="Rinnovi effettuati" value={abb.num_rinnovi != null ? String(abb.num_rinnovi) : "—"} testId="abbonamento-num-rinnovi" />
    </div>
    <ScadenzaAlert abb={abb} />
    <RinnovoButton abb={abb} />
    </>
  );
}
